import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2, ShoppingBag, CheckCircle2 } from "lucide-react";

export default function CartDrawer() {
  const { items, remove, clear, subtotal, open, setOpen } = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);

  const discount = applied ? Math.floor(subtotal * applied.percent / 100) : 0;
  const total = subtotal - discount;

  const applyCoupon = async () => {
    if (!user) { toast.error("Login first"); return; }
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, total: subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied — ${data.percent}% off`);
    } catch (e) {
      setApplied(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const checkout = async () => {
    if (!user) { setOpen(false); window.location.href = "/login"; return; }
    if (items.length === 0) return;
    setPlacing(true);
    try {
      const payload = { items: items.map((i) => ({ kind: i.kind, ref_id: i.ref_id, quantity: i.quantity, duration_id: i.duration_id })), coupon_code: applied?.code };
      const { data } = await api.post("/checkout", payload);
      setPlaced(data.order);
      clear();
      setApplied(null);
      setCoupon("");
      toast.success("Payment successful (mock)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setPlacing(false); }
  };

  const closeAll = () => { setPlaced(null); setOpen(false); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#FAF7F2] w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#5C061C] flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> {t("cart.title")}
          </SheetTitle>
        </SheetHeader>

        {placed ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 p-6" data-testid="order-success">
            <CheckCircle2 className="h-16 w-16 text-[#15803D]" />
            <h3 className="font-serif text-2xl text-[#5C061C]">Payment Successful</h3>
            <p className="text-sm text-[#6E6259]">Txn: <span className="font-mono">{placed.razorpay_txn_id}</span></p>
            {placed.pickup_code && (
              <div className="mt-2 rounded-lg border border-dashed border-[#D97706] bg-white p-4 w-full">
                <div className="text-xs uppercase tracking-widest text-[#B45309]">Pickup code</div>
                <div className="font-serif text-3xl text-[#5C061C] mt-1">{placed.pickup_code}</div>
                <div className="text-xs text-[#6E6259] mt-2">Show this at Sanstha office, Thane</div>
              </div>
            )}
            <p className="text-xs text-[#6E6259]">{t("cart.non_refundable")}</p>
            <Button onClick={closeAll} className="btn-maroon rounded-full mt-2 h-11 px-6" data-testid="order-close-button">{t("common.close")}</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-[#6E6259]">
            <div className="text-center">
              <ShoppingBag className="h-12 w-12 mx-auto opacity-40" />
              <p className="mt-3">{t("cart.empty")}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 mt-4">
              {items.map((it, idx) => (
                <div key={idx} className="card-warm rounded-lg p-3 flex gap-3" data-testid={`cart-item-${idx}`}>
                  {it.image && <img src={it.image} alt="" className="h-16 w-16 rounded-md object-cover" />}
                  <div className="flex-1">
                    <div className="font-medium text-[#1F1A17]">{it.name}</div>
                    <div className="text-xs text-[#6E6259]">
                      {it.kind === "book" ? `Rental • ${it.duration_label}` : `Qty: ${it.quantity}`}
                    </div>
                    <div className="text-[#5C061C] font-semibold mt-1">₹{it.unit_price * it.quantity}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(idx)} data-testid={`cart-item-remove-${idx}`}>
                    <Trash2 className="h-4 w-4 text-[#DC2626]" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E8DEC8] pt-4 space-y-3">
              <div className="flex gap-2">
                <Input placeholder={t("cart.coupon")} value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="bg-white border-[#E8DEC8]" data-testid="coupon-input" />
                <Button variant="outline" onClick={applyCoupon} className="border-[#E8DEC8]" data-testid="coupon-apply-button">{t("cart.apply")}</Button>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span>₹{subtotal}</span></div>
                {applied && <div className="flex justify-between text-[#15803D]"><span>{t("cart.discount")} ({applied.code})</span><span>−₹{discount}</span></div>}
                <div className="flex justify-between font-semibold text-lg text-[#5C061C]"><span>{t("cart.total")}</span><span>₹{total}</span></div>
              </div>
              <p className="text-xs text-[#6E6259]">{t("cart.non_refundable")}</p>
              <Button onClick={checkout} disabled={placing} className="btn-saffron w-full h-12 rounded-full text-base" data-testid="cart-pickup-checkout-button">
                {placing ? "Processing..." : t("cart.checkout")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
